import { useMemo, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, type CheckDefinition, type Lookup, type WorkOrder } from '../lib/api'
import { money, today } from '../lib/format'
import { Panel, Field, Alert, Tag } from '../components/ui'
import Combo from '../components/Combo'
import AcceptanceDoc from './print/AcceptanceDoc'

/** 可以建立驗收單的發包單狀態，與後端 prefill 的把關一致 */
const PENDING_STATUSES = '已交稿,驗收中'

interface Prefill {
  work_order: any
  items: any[]
  articles: any[]
  check_definitions: CheckDefinition[]
  next_batch_no: number
}

interface ItemRow {
  /** 這篇要不要進「本批次」。取消勾選的留到下次帶出同一張發包單時再出現 */
  include: boolean
  seq: number
  item_id: number
  article_id: number | null
  client_id: number
  client_name: string
  contract_code: string | null
  title: string
  keyword: string
  word_target: number | null
  word_actual: number | null
  deliver_link: string
  result: '通過' | '退修'
  unit_price: number
}

const CONCLUSIONS = ['驗收通過', '有條件通過', '退修', '驗收不通過'] as const

export default function AcceptanceForm() {
  const nav = useNavigate()
  const [sp] = useSearchParams()
  const [code, setCode] = useState('')
  /** 待驗收發包單清單，給下拉選；清單外的單號仍可手動輸入 */
  const [pending, setPending] = useState<WorkOrder[]>([])
  const [woId, setWoId] = useState<number | null>(null)
  const [pre, setPre] = useState<Prefill | null>(null)
  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [rows, setRows] = useState<ItemRow[]>([])
  const [checks, setChecks] = useState<Record<string, { verdict: '符合' | '不符'; note: string }>>({})
  const [header, setHeader] = useState({
    reviewer_id: null as number | null,
    delivered_on: today(),
    reviewed_on: today(),
    /** 請款月份。預設跟著驗收日期走，使用者改過之後就不再自動連動 */
    billing_period: today().slice(0, 7),
    deliver_link: '',
    conclusion: '驗收通過' as (typeof CONCLUSIONS)[number],
    fix_due_days: null as number | null,
    remark: '',
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(false)
  /** 使用者手動指定過請款月份就不再跟著驗收日期跑 */
  const [billingTouched, setBillingTouched] = useState(false)

  useEffect(() => {
    api.get<Lookup>('/masters/lookup').then(setLookup).catch(() => {})
    // 刻意不帶月份：上月已交稿、這個月才驗是常態，篩月份反而會讓要驗的單消失
    api
      .get<WorkOrder[]>(`/work-orders?status=${encodeURIComponent(PENDING_STATUSES)}`)
      .then(setPending)
      .catch(() => {})
  }, [])

  // 收參數而不是讀 code state：setCode() 後馬上呼叫會讀到舊值
  const load = async (raw?: string) => {
    const c = (raw ?? code).trim()
    if (!c) return
    setError('')
    try {
      const p = await api.get<Prefill>(`/acceptances/prefill/${encodeURIComponent(c)}`)
      setPre(p)
      setHeader((h) => ({
        ...h,
        deliver_link: p.work_order.deliver_link ?? '',
        // 換一張單時，前一張選好的驗收人若正好是這張的承包人本人，要清掉
        reviewer_id: h.reviewer_id && h.reviewer_id === p.work_order.writer_staff_id ? null : h.reviewer_id,
      }))

      /*
       * 文章需求明細若有填就逐篇帶出；否則依「件數」展開空白列。
       *
       * 配對不能只看 article.item_id —— 發包單存檔時不會寫這個關聯，實際資料多半是 NULL，
       * 只認 item_id 的話標題會整片帶不出來。所以退而求其次用客戶比對，
       * 並用 used 記錄配過的篇，避免同一篇被兩個明細列重複帶走。
       */
      const built: ItemRow[] = []
      const used = new Set<number>()
      const single = p.items.length === 1
      let seq = 1
      for (const it of p.items) {
        const arts = p.articles.filter((a: any) =>
          used.has(a.id) ? false
            : a.item_id != null ? a.item_id === it.id
            : single || a.client_id == null || a.client_id === it.client_id
        )
        const done = it.accepted_qty ?? 0
        const n = Math.max(it.qty - done, 0)
        for (let k = 0; k < n; k++) {
          // 已驗過的篇數要跳過，第二批帶出來的才會是還沒驗的那幾篇
          const a = arts[done + k]
          if (a) used.add(a.id)
          built.push({
            include: true,
            seq: seq++,
            item_id: it.id,
            article_id: a?.id ?? null,
            client_id: it.client_id,
            client_name: it.client_name,
            contract_code: it.contract_code,
            title: a?.title ?? '',
            keyword: a?.keyword ?? '',
            word_target: a?.word_target ?? 1200,
            word_actual: null,
            deliver_link: '',
            result: '通過',
            unit_price: it.unit_price,
          })
        }
      }
      setRows(built)

      const init: Record<string, { verdict: '符合' | '不符'; note: string }> = {}
      for (const d of p.check_definitions) init[d.code] = { verdict: '符合', note: '' }
      setChecks(init)
    } catch (e: any) {
      setPre(null)
      setError(e.message)
    }
  }

  /**
   * 從待辦頁點進來時網址會帶 ?wo=發包單號，直接帶出，不用再按一次「帶出」。
   * deps 留空只跑一次 —— 放 sp 的話使用者改單號後會被網址覆蓋回去。
   */
  useEffect(() => {
    const c = sp.get('wo')
    if (c) { setCode(c); load(c) }
  }, [])

  /** 清單通常比網址慢回來，補上對應的 id，下拉才不會是空的 */
  useEffect(() => {
    if (woId || !code || pending.length === 0) return
    const hit = pending.find((w) => w.code === code)
    if (hit) setWoId(hit.id)
  }, [pending, code, woId])

  /** 沒勾「本批次」的篇數完全不進這張驗收單，篇數、金額、列印版都不算它 */
  const batch = useMemo(() => rows.filter((r) => r.include), [rows])
  const passed = useMemo(() => batch.filter((r) => r.result === '通過'), [batch])
  const amount = useMemo(() => passed.reduce((n, r) => n + r.unit_price, 0), [passed])

  /*
   * 內包案件（承包人就是自家員工）不得由承包人自驗 ——
   * 驗收人和承包人同一個人，這張單就沒有內控意義。
   * writer_staff_id 是承包人在人員名冊的 id，沒對應到就是 null（外部寫手一律 null）。
   */
  const selfStaffId: number | null = pre?.work_order.writer_staff_id ?? null
  const isInternal = pre?.work_order.writer_kind === '內部發包'

  const setRow = (idx: number, patch: Partial<ItemRow>) =>
    setRows((list) => list.map((r, i) => (i === idx ? { ...r, ...patch } : r)))

  const submit = async () => {
    setError('')
    try {
      if (!pre) throw new Error('請先輸入發包單號帶出明細')
      if (rows.length === 0) throw new Error('沒有可驗收的篇數（此發包單可能已全數驗收完畢）')
      if (batch.length === 0) throw new Error('「本批次」至少要勾選一篇')
      if (isInternal && !header.reviewer_id) throw new Error('內包案件請指定驗收人')
      if (selfStaffId && header.reviewer_id === selfStaffId)
        throw new Error(
          `內包案件的驗收人不得為承包人本人（${pre!.work_order.writer_name}），請改由主管或另一位負責人驗收`
        )
    } catch (e: any) {
      setError(e.message)
      return
    }

    /*
     * 建立驗收單會連動回填發包單、產生請款列，都是不可逆的動作，
     * 所以把原本畫在表單裡的那段說明改成送出前的確認視窗 ——
     * 寫在畫面上容易被滑過去，跳出來才會真的看到。
     */
    const willBill = header.conclusion === '驗收通過' || header.conclusion === '有條件通過'
    const confirmed = window.confirm(
      [
        `發包單 ${pre!.work_order.code}　第 ${pre!.next_batch_no} 批`,
        `本批次 ${batch.length} 篇，其中通過 ${passed.length} 篇`,
        `驗收結論：${header.conclusion}`,
        '',
        '建立後系統會自動：',
        '・回填發包明細的已驗收篇數',
        `・更新發包單狀態${header.conclusion === '退修' ? '（回到「待交稿」，退修次數 +1）' : ''}`,
        ...(willBill ? [`・將通過的 ${passed.length} 篇（${money(amount)} 元）寫入請款明細`] : []),
        '',
        '確定要建立嗎？',
      ].join('\n')
    )
    if (!confirmed) return

    setSaving(true)
    try {
      const res = await api.post<any>('/acceptances', {
        work_order_id: pre!.work_order.id,
        reviewer_id: header.reviewer_id,
        batch_no: pre!.next_batch_no,
        due_on: pre!.work_order.due_on,
        delivered_on: header.delivered_on,
        reviewed_on: header.reviewed_on,
        billing_period: header.billing_period,
        deliver_link: header.deliver_link,
        conclusion: header.conclusion,
        fix_due_days: header.fix_due_days,
        remark: header.remark,
        items: batch.map((r, i) => ({ ...r, seq: i + 1 })),
        checks: Object.entries(checks).map(([c, v]) => ({ code: c, verdict: v.verdict, note: v.note })),
      })
      alert(`驗收單已建立：${res.code}`)
      nav(`/acceptances/${res.id}/print`)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }


  /**
   * 存檔前的預覽用資料，欄位名稱刻意跟 GET /acceptances/:id 回來的一致，
   * 這樣同一個 AcceptanceDoc 不必分辨是草稿還是已存檔的單。
   * 單號是後端產的，這裡還沒有，填一段說明文字而不是留白。
   */
  const draft = useMemo(() => {
    if (!pre) return null
    return {
      code: '（建立後自動產生）',
      writer_name: pre.work_order.writer_name,
      work_order_code: pre.work_order.code,
      dispatch_type: pre.work_order.dispatch_type,
      batch_no: pre.next_batch_no,
      due_on: pre.work_order.due_on,
      delivered_on: header.delivered_on,
      reviewed_on: header.reviewed_on,
      reviewer_name: lookup?.staff.find((s) => s.id === header.reviewer_id)?.name ?? '',
      deliver_link: header.deliver_link,
      conclusion: header.conclusion,
      fix_due_days: header.fix_due_days,
      remark: header.remark,
      amount,
      passed_qty: passed.length,
      items: batch,
      checks: pre.check_definitions.map((d) => ({
        code: d.code,
        category: d.category,
        label: d.label,
        verdict: checks[d.code]?.verdict ?? '符合',
        note: checks[d.code]?.note ?? '',
      })),
    }
  }, [pre, header, lookup, amount, passed, batch, checks])

  if (preview && draft) {
    return (
      <>
        <div className="page-head no-print">
          <h2>驗收單列印預覽（尚未建立）</h2>
          <div className="spacer" />
          <div className="btn-row">
            <button className="primary" onClick={() => window.print()}>列印 / 存成 PDF</button>
            <button onClick={() => setPreview(false)}>返回編輯</button>
          </div>
        </div>
        <AcceptanceDoc a={draft} />
      </>
    )
  }

  return (
    <>
      <div className="page-head">
        <h2>新增驗收單</h2>
        <span className="hint">選擇或輸入發包單號自動帶出待驗收明細</span>
        <div className="spacer" />
        {/* 帶出發包單前沒東西可建也沒東西可預覽，按鈕跟著明細一起出現 */}
        {pre && (
          <div className="btn-row">
            {/* 預覽只是切換畫面，表單狀態留在 React 裡，返回編輯不會掉資料 */}
            <button onClick={() => setPreview(true)}>列印預覽</button>
            <button className="primary" onClick={submit} disabled={saving}>
              {saving ? '建立中…' : '建立驗收單'}
            </button>
          </div>
        )}
      </div>

      <Alert kind="error">{error}</Alert>

      <Panel title="帶出發包單">
        <div className="btn-row">
          <div style={{ width: 260 }}>
            <Combo
              items={pending}
              value={woId}
              label={(w) => w.code}
              // 打單號或寫手名字都找得到；沒打字時不展開清單
              search={(w) => `${w.code} ${w.writer_name ?? ''} ${w.writer_name_en ?? ''}`}
              maxSuggestions={20}
              sub={(w) => `${w.writer_name ?? '—'} · ${w.status} · ${w.total_qty} 篇`}
              placeholder="輸入單號或寫手名字"
              allowEmpty
              onChange={(item, raw) => {
                setWoId(item?.id ?? null)
                const c = item ? item.code : raw.trim()
                setCode(c)
                if (item) load(c)   // 選到清單裡的單就直接帶出
              }}
            />
          </div>
          <button className="primary" onClick={() => load()}>帶出</button>
          {pre && (
            <>
              <Tag value={pre.work_order.status} />
              <span className="hint">
                {pre.work_order.code} ／
                承包人 {pre.work_order.writer_name} {pre.work_order.writer_name_en} ／
                約定交件日 {pre.work_order.due_on ?? '—'} ／
                第 {pre.next_batch_no} 批
              </span>
            </>
          )}
        </div>
        <p className="hint" style={{ margin: '8px 0 0', fontSize: 12 }}>
          清單裡沒有的單號可直接輸入後按「帶出」；欄位也可以打寫手名字篩選。
        </p>

        {/* 進頁面就把待驗收的單全部列出來，不必先知道單號。點任一列即帶出 */}
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="data">
            <thead>
              <tr>
                <th>發包單號</th>
                <th>承包人</th>
                <th>客戶</th>
                <th>約定交件日</th>
                <th className="num" style={{ width: 110 }}>已驗／總篇數</th>
                <th style={{ width: 80 }}>狀態</th>
                <th style={{ width: 76 }}></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((w) => (
                <tr
                  key={w.id}
                  style={{
                    cursor: 'pointer',
                    ...(w.id === woId ? { background: '#eef4f9' } : null),
                  }}
                  onClick={() => { setWoId(w.id); setCode(w.code); load(w.code) }}
                >
                  <td>{w.code}</td>
                  <td>{w.writer_name} {w.writer_name_en}</td>
                  <td>{w.client_names?.replaceAll(',', '、') ?? '—'}</td>
                  <td>{w.due_on ?? '—'}</td>
                  <td className="num">{w.accepted_qty ?? 0} ／ {w.total_qty} 篇</td>
                  <td><Tag value={w.status} /></td>
                  <td>
                    <button style={{ whiteSpace: 'nowrap' }} onClick={(e) => { e.stopPropagation(); setWoId(w.id); setCode(w.code); load(w.code) }}>
                      帶出
                    </button>
                  </td>
                </tr>
              ))}
              {pending.length === 0 && (
                <tr><td colSpan={7} className="hint">目前沒有待驗收的發包單</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7}>
                  共 {pending.length} 張待驗收（「已交稿」＋「驗收中」，驗收中＝驗了一部分還沒驗完）
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>

      {pre && (
        <>
          <Panel title="一、基本資料">
            {isInternal && (
              <Alert kind="warn">
                {selfStaffId
                  ? `內包案件：承包人 ${pre.work_order.writer_name} 是自家人員，依規定不得自驗，已從驗收人清單中排除，請指派主管或另一位負責人。`
                  : `內包案件：承包人 ${pre.work_order.writer_name} 尚未在寫手名冊對應到員工，系統無法自動排除本人。請確認驗收人不是承包人自己（可到主檔維護補上「對應員工」）。`}
              </Alert>
            )}
            {/* 12 欄制，兩列各自排滿：驗收人3 實際交付日3 驗收日期3 請款月份3 ／ 核定金額3 交付連結9 */}
            <div className="grid c12">
              <Field label="驗收人" span={3}>
                <select value={header.reviewer_id ?? ''} onChange={(e) => setHeader({ ...header, reviewer_id: Number(e.target.value) || null })}>
                  <option value="">— 未指定 —</option>
                  {lookup?.staff.map((s) => (
                    // 承包人本人留在清單裡但停用，讓人看得出是「不能選」而不是「漏了這個人」
                    <option key={s.id} value={s.id} disabled={s.id === selfStaffId}>
                      {s.id === selfStaffId ? `${s.name}（承包人本人，不可驗收）` : s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="實際交付日" span={3}>
                <input type="date" value={header.delivered_on} onChange={(e) => setHeader({ ...header, delivered_on: e.target.value })} />
              </Field>
              <Field label="驗收日期" span={3}>
                <input
                  type="date"
                  value={header.reviewed_on}
                  onChange={(e) =>
                    setHeader((h) => ({
                      ...h,
                      reviewed_on: e.target.value,
                      // 沒手動指定過就跟著走，避免兩個欄位各說各話
                      billing_period: billingTouched ? h.billing_period : e.target.value.slice(0, 7),
                    }))
                  }
                />
              </Field>
              <Field label="請款月份" span={3}>
                <input
                  type="month"
                  value={header.billing_period}
                  onChange={(e) => { setBillingTouched(true); setHeader({ ...header, billing_period: e.target.value }) }}
                />
              </Field>
              <Field label="本批次核定金額（自動）" span={3}>
                <input readOnly value={`${money(amount)} 元 / ${passed.length} 篇`} />
              </Field>
              <Field label="稿件交付連結" span={9}>
                <input value={header.deliver_link} onChange={(e) => setHeader({ ...header, deliver_link: e.target.value })} />
              </Field>
            </div>
          </Panel>

          <Panel title="二、交付文章清單（逐篇判定）">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {/* 欄位與列印版（AcceptanceDoc 的「二、交付文章清單」）一致，
                        填的時候看到什麼，印出來就是什麼 */}
                    <th style={{ width: 34 }}>#</th>
                    <th style={{ width: 62 }}>本批次</th>
                    <th style={{ width: '26%' }}>客戶</th>
                    <th style={{ minWidth: 220 }}>文章標題</th>
                    <th className="num" style={{ width: 60 }}>篇數</th>
                    <th className="num" style={{ width: 90 }}>單價</th>
                    <th style={{ width: 100 }}>單篇結果</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr
                      key={idx}
                      style={
                        !r.include ? { opacity: 0.45 }
                          : r.result === '退修' ? { background: '#fdf6f5' }
                          : undefined
                      }
                    >
                      <td>{idx + 1}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={r.include}
                          onChange={(e) => setRow(idx, { include: e.target.checked })}
                        />
                      </td>
                      <td>{r.client_name}</td>
                      <td>
                        <input
                          value={r.title}
                          disabled={!r.include}
                          onChange={(e) => setRow(idx, { title: e.target.value })}
                        />
                      </td>
                      {/* 一列就是一篇，跟列印版一樣固定 1 */}
                      <td className="num">1</td>
                      <td className="num">{money(r.unit_price)}</td>
                      <td>
                        <select
                          value={r.result}
                          disabled={!r.include}
                          onChange={(e) => setRow(idx, { result: e.target.value as '通過' | '退修' })}
                        >
                          <option value="通過">通過</option>
                          <option value="退修">退修</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="num">實際金額</td>
                    <td className="num">{money(amount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="hint" style={{ margin: '8px 0 0', fontSize: 12 }}>
              本批次 {batch.length} 篇（通過 {passed.length} 篇）。
              「本批次」不勾＝這次先不驗，篇數留到下次帶出同一張發包單時再出現，發包單不受影響；
              勾了但選「退修」＝這次驗過但退件，發包單會回到「待交稿」並累計一次退修。
            </p>
          </Panel>

          {/* 檢核項目改成單一清單（原本分內容品質 A、SEO 規格 B 兩張表）。
              不再依 category 過濾，啟用中的定義有幾項就渲染幾項 */}
          <Panel title="三、品質檢核">
            <CheckTable defs={pre?.check_definitions ?? []} checks={checks} setChecks={setChecks} />
          </Panel>

          <Panel title="四、驗收結論">
            <div className="grid c3">
              <Field label="結論">
                <select value={header.conclusion} onChange={(e) => setHeader({ ...header, conclusion: e.target.value as any })}>
                  {CONCLUSIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="補正／重新交付期限（工作天）">
                <input
                  type="number"
                  value={header.fix_due_days ?? ''}
                  disabled={header.conclusion === '驗收通過'}
                  onChange={(e) => setHeader({ ...header, fix_due_days: e.target.value === '' ? null : Number(e.target.value) })}
                />
              </Field>
              <Field label="本發包單累計退修次數">
                <input readOnly value={pre.work_order.revise_count ?? 0} />
              </Field>
            </div>
            <div style={{ marginTop: 12 }}>
              <Field label="驗收備註／退件與修改紀錄">
                <textarea value={header.remark} onChange={(e) => setHeader({ ...header, remark: e.target.value })} />
              </Field>
            </div>
          </Panel>

        </>
      )}
    </>
  )
}

function CheckTable({
  defs,
  checks,
  setChecks,
}: {
  defs: CheckDefinition[]
  checks: Record<string, { verdict: '符合' | '不符'; note: string }>
  setChecks: (v: any) => void
}) {
  return (
    <table className="data">
      <thead>
        <tr>
          <th style={{ width: 40 }}>編號</th>
          <th>檢查項目</th>
          <th style={{ width: 88 }}>判定</th>
          <th style={{ minWidth: 120 }}>備註／缺失說明</th>
        </tr>
      </thead>
      <tbody>
        {defs.map((d) => {
          const v = checks[d.code] ?? { verdict: '符合', note: '' }
          return (
            <tr key={d.code} style={v.verdict === '不符' ? { background: '#fdf6f5' } : undefined}>
              <td>{d.code}</td>
              <td>{d.label}</td>
              <td>
                <select
                  value={v.verdict}
                  onChange={(e) => setChecks((c: any) => ({ ...c, [d.code]: { ...v, verdict: e.target.value } }))}
                >
                  <option value="符合">符合</option>
                  <option value="不符">不符</option>
                </select>
              </td>
              <td>
                {/* 判定符合也能寫字：常有「已修正後通過」這種要留下來的說明 */}
                <input
                  value={v.note}
                  placeholder={v.verdict === '符合' ? '選填' : '請說明缺失'}
                  onChange={(e) => setChecks((c: any) => ({ ...c, [d.code]: { ...v, note: e.target.value } }))}
                />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
